// Asking the browser where the resident is, and what the screen says while it
// answers (SPEC-redesign §5.8, D-c). In the prototype `◌` is drawn and does
// nothing at all after a denial; here it is a real permission flow.
//
// `src/shared/geo.ts` holds the position once we have one, and — since #279 —
// forgets it on the one word from here that says we may no longer use it
// (`here.clear()`, whose only caller this module is). Everything about *getting*
// a position, and about the permission that governs it, lives here (§4.3).
import { here } from '../shared/geo.ts';

export type GeoState =
  /** nothing has been asked for; the glyph is `◌` and the centre is Stary Rynek */
  | 'idle'
  /** the browser is asking, or looking: `◌` and „szukam…" */
  | 'pending'
  /** we have a fix: `◎ ja`, and distances may be called „od Ciebie" */
  | 'granted'
  /** denied, unavailable or timed out — one outcome for the resident (§5.8) */
  | 'refused';

/** Enough time to get a fix indoors, and not so much that `◌` spins for ever. */
const TIMEOUT_MS = 10_000;

const listeners = new Set<() => void>();
let state: GeoState = 'idle';

/**
 * The one writer of the state, and — since #279 — the one place the last known
 * position is forgotten.
 *
 * `'refused'` clears `here.at`, whichever path reached it: denied, unavailable,
 * timed out, no API at all and a `resume()` that found no standing permission
 * are one outcome for the resident (§5.8), so they are one outcome for the
 * store too. Otherwise „we have a position" and „the resident lets us use it"
 * drift apart: after a grant followed by a refusal the centre stayed on the
 * last fix and every distance was measured from it, while the note underneath
 * said the centre was Stary Rynek.
 *
 * `'pending'` never clears. A re-ask is a question, not an answer, and dropping
 * the fix while the browser is still thinking would move the map for a resident
 * who is about to say yes again.
 *
 * The state is assigned BEFORE the store is cleared, because clearing it paints
 * the band and nothing should hear the store before the state agrees with it.
 * The reachable cost of the other order is smaller than it looks, and saying so
 * is the point: on the `ask()` path `'refused'` is always entered from
 * `'pending'` — `ask()` moves there before it calls the browser, and the error
 * callback is the only way back — so the wrong order would paint `◌` „szukam…"
 * over Stary Rynek, which is wrong but self-consistent. The one entry from
 * `'granted'` is the `!api` branch below, the API vanishing between two taps,
 * and that is the only frame that could read `◎ ja` over a position already
 * gone. So this is a discipline rather than a guard against a contradiction,
 * and `geolocate.test.ts` pins the notification sequence so it cannot quietly
 * invert.
 *
 * The ordering belongs to this path, not to the module — `ask()` fills the
 * store before it moves to `'granted'`, which is the opposite order and always
 * was.
 *
 * The early return below needs no clear of its own, and the reason is
 * `resume()`'s guard rather than anything here: for a fix to land while the
 * state reads `'refused'`, something would have to reach `'refused'` between
 * `moveTo('pending')` and the success callback, and that guard is what removed
 * the last writer that could. Loosen it and this line stops being safe.
 */
function moveTo(next: GeoState) {
  if (state === next) return;
  state = next;
  if (next === 'refused') here.clear();
  for (const fn of listeners) fn();
}

export const geolocate = {
  get: (): GeoState => state,

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },

  /**
   * Ask, every time it is called. A browser that has already been refused
   * rejects immediately and we show the note again; one that is willing to ask
   * again does, which is the whole of „tapping `◌` again re-asks when the
   * browser still allows it, otherwise shows the note again" (§5.8).
   */
  ask(): void {
    if (state === 'pending') return;
    // The types promise this is always there; a browser with the API switched
    // off, or a test double, does not. A missing API is a refusal, not a crash.
    const { geolocation: api } = navigator as { geolocation?: Geolocation };
    if (!api) {
      moveTo('refused');
      return;
    }
    moveTo('pending');
    api.getCurrentPosition(
      (pos) => {
        here.set({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        moveTo('granted');
      },
      () => {
        // Denied, position unavailable and timed out are three reasons and one
        // outcome: we do not know where the resident is, so we do not pretend
        // any distance is measured from them.
        moveTo('refused');
      },
      { timeout: TIMEOUT_MS, maximumAge: 60_000 },
    );
  },

  /**
   * A link that arrives carrying `tu=ja`, at boot (§5.8).
   *
   * It never prompts. A permission prompt with no gesture behind it is hostile,
   * and browsers punish a page that asks for one on load — so the address alone
   * may not make the browser ask. If the permission is already granted we
   * locate silently and the link opens as the sender meant it; otherwise the
   * centre stays Stary Rynek, the glyph stays `◌`, and the resident is told
   * why in the note that is already there. Tapping `◌` still asks, as always,
   * and that is a gesture.
   */
  async resume(): Promise<void> {
    const { permissions } = navigator as {
      permissions?: { query?: (d: { name: string }) => Promise<{ state: string }> };
    };
    let permission: string | null = null;
    try {
      // Firefox has no `geolocation` descriptor and throws on it; a browser
      // with no Permissions API at all simply has nothing to tell us. Neither
      // is a reason to ask, and neither is a reason to fail.
      permission = (await permissions?.query?.({ name: 'geolocation' }))?.state ?? null;
    } catch {
      permission = null;
    }
    if (permission === 'granted') {
      this.ask();
      return;
    }
    // Not refused by this resident, necessarily — but the fact they are shown
    // is the same one: we do not have their position, so the centre stayed
    // where it was and no distance is measured from them.
    //
    // Only while nothing else has spoken. `query()` is awaited, and a tap on
    // `◌` inside that window is a gesture; since #279 a refusal FORGETS the
    // fix, so a stale resume landing on a grant would throw away the position
    // the resident had just given. Resume speaks for a boot nobody has
    // answered yet, and `'idle'` is what that looks like.
    if (state === 'idle') moveTo('refused');
  },

  /**
   * For the tests alone: the module is a singleton because the permission is.
   *
   * It forgets the position too, or it would hand back the very state this
   * module exists to rule out — `'idle'` with a fix in hand, a position nobody
   * gave us permission for.
   */
  reset(): void {
    state = 'idle';
    here.clear();
  },
};
